import { ReactNode, useMemo } from 'react';
import { ThemeProvider } from 'styled-components';

import { Box } from 'components/Box';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { AcceptCookiesContainer } from 'containers/AcceptCookiesContainer';
import { ClientOnly } from 'containers/ClientOnly';
import FixedSpacerContainer from 'containers/FixedSpacerContainer';
import FlashMessageContainer from 'containers/FlashMessageContainer';
import { ModalRoot } from 'containers/Modal/ModalRoot';
import { NoAmp } from 'containers/NoAmp';
import { colors, fontSizesByType, themeContextValue } from 'theme';
import { isDefined } from 'utils/functional';
import { getItem } from 'utils/localStorage';

import { ButtonBar, ButtonBarContainer } from './ButtonBar';
import { PageContent } from './PageContent';
import { Root } from './Root';

const testMode = getItem('testMode');

type CSSPosition = 'static' | 'sticky';

type Props = {
  children: ReactNode;
  header?: ReactNode;
  buttonBar?: ReactNode;
  centered?: boolean;
  stickyHeaderHeight?: (string | number)[];
};

export function BaseLayout({
  children,
  header,
  buttonBar,
  centered,
  stickyHeaderHeight,
}: Props) {
  const flashMessageContainerPosition: CSSPosition | CSSPosition[] =
    useMemo(() => {
      if (testMode || !isDefined(stickyHeaderHeight)) return 'static';

      if (typeof stickyHeaderHeight === 'number') return 'sticky';

      return stickyHeaderHeight.map((height) =>
        isDefined(height) ? 'sticky' : 'static'
      );
    }, [stickyHeaderHeight]);

  return (
    <ThemeProvider theme={themeContextValue}>
      <Root
        bg={centered && colors.backgroundGrey}
        fontSize={fontSizesByType.body}
        id="layout-root"
      >
        <Box classes={['flex']} minHeight="100vh" flexDirection="column">
          {header}

          <ClientOnly>
            <Box
              data-qa-id="some-element"
              position={flashMessageContainerPosition}
              top={stickyHeaderHeight}
              zIndex={3}
            >
              <FlashMessageContainer />
            </Box>
          </ClientOnly>

          <PageContent
            classes={['flex']}
            flexDirection="column"
            flex="1 1 auto"
            $centered={centered}
          >
            {children}
          </PageContent>
        </Box>

        {buttonBar && (
          <NoAmp>
            <PrintHidden>
              <ButtonBarContainer>
                <ButtonBar classes={['flex', 'justify-center', 'align-center']}>
                  {buttonBar}
                </ButtonBar>
              </ButtonBarContainer>
            </PrintHidden>
          </NoAmp>
        )}

        <FixedSpacerContainer
          bottom={0}
          left={0}
          zIndex={80}
          transform="translateZ(0)"
          width="100%"
        >
          <ClientOnly>
            <AcceptCookiesContainer />
          </ClientOnly>
        </FixedSpacerContainer>

        <ModalRoot />
      </Root>
    </ThemeProvider>
  );
}
